// Client side of the Assembly event relay (apps/server/src/relay.ts).
// The server holds one Socket Mode connection per Slack app and fans events
// out to every connected client the event is authorised for.

export type RelayClientMessage = { type: 'hello'; token: string }

export type RelayServerMessage =
  | { type: 'ready'; team_id: string; user_id: string }
  | { type: 'error'; error: string }
  | { type: 'event'; event: SlackEvent }

/** Shape of an Events API inner event (loosely typed: there are many kinds). */
export interface SlackEvent {
  type: string
  subtype?: string
  channel?: string | { id: string; name?: string }
  user?: string | { id: string }
  ts?: string
  event_ts?: string
  thread_ts?: string
  item?: { type: string; channel: string; ts: string }
  reaction?: string
  message?: any
  previous_message?: any
  deleted_ts?: string
  [key: string]: unknown
}

export interface RelayConnection {
  close(): void
}

/**
 * Connects to the relay, reconnecting with backoff. `onStatus(true)` fires once the
 * server has accepted the token; `onStatus(false)` whenever the connection drops.
 */
export function connectRelay(
  url: string,
  token: string,
  handlers: { onEvent: (event: SlackEvent) => void; onStatus?: (live: boolean) => void; onFatal?: (error: string) => void },
): RelayConnection {
  let socket: WebSocket | null = null
  let closed = false
  let attempt = 0
  let timer: ReturnType<typeof setTimeout> | undefined

  const open = () => {
    if (closed) return
    socket = new WebSocket(url)
    socket.onopen = () => socket?.send(JSON.stringify({ type: 'hello', token } satisfies RelayClientMessage))
    socket.onmessage = (e) => {
      let msg: RelayServerMessage
      try {
        msg = JSON.parse(String(e.data))
      } catch {
        return
      }
      if (msg.type === 'ready') {
        attempt = 0
        handlers.onStatus?.(true)
      } else if (msg.type === 'event') {
        handlers.onEvent(msg.event)
      } else if (msg.type === 'error') {
        // Relay disabled or token rejected: stop retrying, the client will poll instead.
        closed = true
        handlers.onFatal?.(msg.error)
        socket?.close()
      }
    }
    socket.onclose = () => {
      handlers.onStatus?.(false)
      if (closed) return
      const delay = Math.min(30_000, 1000 * 2 ** attempt++)
      timer = setTimeout(open, delay)
    }
    socket.onerror = () => socket?.close()
  }
  open()

  return {
    close() {
      closed = true
      clearTimeout(timer)
      socket?.close()
    },
  }
}
